import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Trash2, Shield, ShieldCheck, User, Users as UsersIcon, KeyRound } from 'lucide-react';
import { getInitials, getAvatarColor, formatDate, roleLabels } from '../lib/utils';
import { toast } from 'sonner';

/**
 * Miroir local des comptes Keycloak.
 *
 * Il n'y a plus d'import d'annuaire (Graph API) ni de sélecteur de rôle : les
 * comptes et les droits sont administrés dans le backoffice de
 * portal.elikateam.com. Le rôle est réécrit depuis les claims du realm à chaque
 * connexion — une modification locale serait écrasée sans prévenir.
 *
 * Restent utiles ici : voir qui s'est déjà connecté, couper l'accès à
 * l'application sans toucher au realm, et purger un compte.
 */

const roleConfig = {
  admin:      { icon: ShieldCheck, iconClass: 'icon-bg-purple', badgeClass: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' },
  manager:    { icon: Shield,      iconClass: 'icon-bg-blue',   badgeClass: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' },
  commercial: { icon: User,        iconClass: 'icon-bg-green',  badgeClass: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' },
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = useCallback(async () => {
    try {
      const r = await usersAPI.list();
      setUsers(r.data);
    } catch {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleDelete = async (id) => {
    if (id === currentUser?.id) { toast.error('Impossible de supprimer votre propre compte'); return; }
    if (!window.confirm(
      'Supprimer ce compte ainsi que toutes les données qui lui sont rattachées '
      + '(contacts, opportunités, devis, factures, produits, tickets) ?\n\n'
      + 'Pour couper simplement l\'accès, désactivez le compte.'
    )) return;
    try { await usersAPI.delete(id); toast.success('Compte supprimé'); loadUsers(); }
    catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleToggleActive = async (u) => {
    if (u.id === currentUser?.id) { toast.error('Impossible de désactiver votre propre compte'); return; }
    try {
      await usersAPI.setActive(u.id, !u.is_active);
      toast.success(u.is_active ? 'Accès désactivé' : 'Accès réactivé');
      loadUsers();
    } catch { toast.error('Erreur'); }
  };

  if (loading) {
    return (
      <div className="space-y-5" data-testid="users-loading">
        <div className="flex items-center justify-between"><Skeleton className="h-7 w-40" /><Skeleton className="h-10 w-52" /></div>
        <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  const stats = [
    { key: 'admin',      label: 'Administrateurs', count: users.filter(u => u.role === 'admin').length,      icon: ShieldCheck, iconClass: 'icon-bg-purple' },
    { key: 'commercial', label: 'Commerciaux',     count: users.filter(u => u.role !== 'admin').length,      icon: User,        iconClass: 'icon-bg-green' },
    { key: 'total',      label: 'Comptes',         count: users.length,                                      icon: UsersIcon,   iconClass: 'icon-bg-blue' },
  ];

  const emptyLabel = 'Aucun compte — les comptes apparaissent à la première connexion';

  return (
    <div className="space-y-5 animate-fade-in" data-testid="users-page">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Utilisateurs</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Comptes ayant ouvert une session sur SalePulse
          </p>
        </div>
      </div>

      {/* Bandeau d'explication — évite de chercher un bouton « ajouter » disparu */}
      <div className="flex items-start gap-3 p-4 bg-muted/40 border border-border rounded-xl">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <KeyRound size={16} className="text-primary" />
        </div>
        <p className="text-sm text-muted-foreground">
          Les comptes et les rôles sont gérés dans Keycloak, via le backoffice du{' '}
          <a href="https://portal.elikateam.com" target="_blank" rel="noreferrer"
             className="text-primary hover:underline font-medium">portail ELIKA</a>.
          Un compte apparaît ici à sa première connexion, et le rôle{' '}
          <code className="text-xs px-1 py-0.5 rounded bg-muted font-mono">salepulse-admin</code>{' '}
          du realm donne les droits d'administration. Le rôle est resynchronisé à chaque
          connexion : le modifier ici serait sans effet.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {stats.map(({ key, label, count, icon: Icon, iconClass }) => (
          <div key={key} className="bg-card border border-border rounded-xl p-3 sm:p-4 shadow-sm flex items-center gap-2 sm:gap-4">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconClass}`}>
              <Icon size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-foreground">{count}</p>
              <p className="text-[10px] sm:text-sm text-muted-foreground truncate">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile list */}
      <div className="md:hidden space-y-3">
        {users.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm">{emptyLabel}</div>
        ) : (
          users.map(u => {
            const { badgeClass } = roleConfig[u.role] || roleConfig.commercial;
            const isCurrentUser = u.id === currentUser?.id;
            return (
              <div key={u.id} className="bg-card border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-9 w-9 flex-shrink-0">
                      <AvatarFallback className={`text-xs font-semibold ${getAvatarColor(u.name)}`}>
                        {getInitials(u.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-foreground text-sm">{u.name}</span>
                        {isCurrentUser && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-border text-muted-foreground">Vous</span>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${badgeClass}`}>
                    {roleLabels[u.role]}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Switch checked={u.is_active} onCheckedChange={() => handleToggleActive(u)} disabled={isCurrentUser} />
                    <span className="text-xs text-muted-foreground">{u.is_active ? 'Actif' : 'Inactif'}</span>
                  </div>
                  <button onClick={() => handleDelete(u.id)} disabled={isCurrentUser}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-30">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {['Utilisateur', 'Email', 'Rôle', 'Créé le', 'Actif', 'Actions'].map(h => (
                  <th key={h} className="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">{emptyLabel}</td></tr>
              ) : (
                users.map(u => {
                  const { badgeClass } = roleConfig[u.role] || roleConfig.commercial;
                  const isCurrentUser = u.id === currentUser?.id;
                  return (
                    <tr key={u.id} className="hover:bg-muted/30 transition-colors" data-testid={`user-row-${u.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className={`text-xs font-semibold ${getAvatarColor(u.name)}`}>
                              {getInitials(u.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground">{u.name}</span>
                            {isCurrentUser && <span className="text-xs px-2 py-0.5 rounded-full border border-border text-muted-foreground font-medium">Vous</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-muted-foreground">{u.email}</td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${badgeClass}`}>
                          {roleLabels[u.role]}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-muted-foreground">{formatDate(u.created_at)}</td>
                      <td className="px-5 py-4">
                        <Switch checked={u.is_active} onCheckedChange={() => handleToggleActive(u)} disabled={isCurrentUser} data-testid={`toggle-user-${u.id}`} />
                      </td>
                      <td className="px-5 py-4">
                        <button onClick={() => handleDelete(u.id)} disabled={isCurrentUser}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {users.length > 0 && (
          <div className="px-5 py-3 border-t border-border bg-muted/20">
            <p className="text-xs text-muted-foreground">{users.length} compte{users.length > 1 ? 's' : ''}</p>
          </div>
        )}
      </div>
    </div>
  );
}
